// Golf Course API Integration
// This utility handles API calls to golfcourseapi.com

const GOLF_COURSE_API_BASE_URL = 'https://api.golfcourseapi.com/v1';
const API_KEY = 'JDBX75CLXYKAEBKZCYOUUO46WE';

// Test API connection and search for a specific course
export const testGolfCourseAPI = async (courseName, city, state) => {
  try {
    console.log('Testing Golf Course API...');
    console.log('Searching for:', courseName, city, state);
    
    // Use the correct v1/search endpoint with search_query parameter
    const searchQuery = `${courseName} ${city} ${state}`.trim();
    const endpoint = `/search?search_query=${encodeURIComponent(searchQuery)}`;
    
    console.log(`Using endpoint: ${endpoint}`);
    console.log(`Search query: ${searchQuery}`);
    
    const response = await fetch(`${GOLF_COURSE_API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Authorization': `Key ${API_KEY}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    console.log('Response status:', response.status);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));
    
    if (response.ok) {
      const data = await response.json();
      console.log('API Response:', data);
      return { success: true, data, endpoint };
    } else {
      const errorData = await response.text();
      console.log('API Error:', errorData);
      return { success: false, error: errorData };
    }
  } catch (error) {
    console.error('Golf Course API test failed:', error);
    return { success: false, error: error.message };
  }
};

// Search for golf courses by various criteria
export const searchGolfCourses = async (searchParams) => {
  const { name, city, state, country } = searchParams;
  
  try {
    // Build search query from parameters
    const searchQuery = [name, city, state, country].filter(Boolean).join(' ');
    console.log('Searching with query:', searchQuery);
    
    const endpoint = `/search?search_query=${encodeURIComponent(searchQuery)}`;
    const response = await fetch(`${GOLF_COURSE_API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Authorization': `Key ${API_KEY}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      return { success: true, data };
    } else {
      const errorData = await response.text();
      return { success: false, error: errorData };
    }
  } catch (error) {
    console.error('Golf course search failed:', error);
    return { success: false, error: error.message };
  }
};

// Get course details by ID
export const getCourseDetails = async (courseId) => {
  try {
    const response = await fetch(`${GOLF_COURSE_API_BASE_URL}/courses/${courseId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Key ${API_KEY}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      return { success: true, data };
    } else {
      const errorData = await response.text();
      return { success: false, error: errorData };
    }
  } catch (error) {
    console.error('Get course details failed:', error);
    return { success: false, error: error.message };
  }
};

// Get all available tees from API course data
export const getAvailableTees = (apiCourse) => {
  const tees = apiCourse.tees || {};
  const availableTees = [];
  
  if (tees.male && Array.isArray(tees.male)) {
    tees.male.forEach((tee, index) => {
      availableTees.push({
        type: 'male',
        index: index,
        name: tee.tee_name || `Male Tee ${index + 1}`,
        color: tee.tee_color || 'unknown',
        par: tee.par_total || 72,
        yardage: tee.total_yards || 0,
        rating: tee.course_rating || 0,
        slope: tee.slope_rating || 0,
        data: tee
      });
    });
  }
  
  if (tees.female && Array.isArray(tees.female)) {
    tees.female.forEach((tee, index) => {
      availableTees.push({
        type: 'female',
        index: index,
        name: tee.tee_name || `Female Tee ${index + 1}`,
        color: tee.tee_color || 'unknown',
        par: tee.par_total || 72,
        yardage: tee.total_yards || 0,
        rating: tee.course_rating || 0,
        slope: tee.slope_rating || 0,
        data: tee
      });
    });
  }
  
  return availableTees;
};

// Convert API course data to our internal format
export const convertAPICourseToInternal = (apiCourse, selectedTeeIndex = 0) => {
  // Get the best tee box data (prefer male tees, fallback to female)
  const tees = apiCourse.tees || {};
  const availableTees = getAvailableTees(apiCourse);
  
  // Use the first available tee if no specific selection
  const selectedTee = availableTees[selectedTeeIndex];
  const maleTees = tees.male && tees.male.length > 0 ? tees.male[0] : null;
  const femaleTees = tees.female && tees.female.length > 0 ? tees.female[0] : null;
  const bestTees = selectedTee?.data || maleTees || femaleTees;
  
  // Convert holes data from API format, ensuring handicap index is imported
  const holes = bestTees?.holes ? bestTees.holes.map((hole, index) => ({
    holeNumber: index + 1,
    par: hole.par || 4,
    index: hole.handicap || hole.hcp || hole.stroke_index || index + 1, // Try multiple field names
    yardage: hole.yardage || hole.yards || 0,
    meters: hole.meters || 0
  })) : generateDefaultHoles(bestTees?.par_total || 72);

  return {
    name: apiCourse.course_name || apiCourse.club_name || '',
    address: apiCourse.location?.address || '',
    city: apiCourse.location?.city || '',
    state: apiCourse.location?.state || '',
    country: apiCourse.location?.country || 'USA',
    latitude: apiCourse.location?.latitude || null,
    longitude: apiCourse.location?.longitude || null,
    totalPar: bestTees?.par_total || 72,
    totalYardage: bestTees?.total_yards || 0,
    totalMeters: bestTees?.total_meters || 0,
    holes: holes,
    // Course rating and slope data
    courseRating: bestTees?.course_rating || 0,
    slopeRating: bestTees?.slope_rating || 0,
    bogeyRating: bestTees?.bogey_rating || 0,
    // Front/Back nine data
    frontCourseRating: bestTees?.front_course_rating || 0,
    frontSlopeRating: bestTees?.front_slope_rating || 0,
    frontBogeyRating: bestTees?.front_bogey_rating || 0,
    backCourseRating: bestTees?.back_course_rating || 0,
    backSlopeRating: bestTees?.back_slope_rating || 0,
    backBogeyRating: bestTees?.back_bogey_rating || 0,
    // Tee information
    availableTees: availableTees,
    selectedTee: selectedTee?.name || bestTees?.tee_name || 'Default',
    teeColor: selectedTee?.color || bestTees?.tee_color || 'unknown',
    // API metadata
    apiId: apiCourse.id || '',
    clubName: apiCourse.club_name || '',
    lastUpdated: new Date()
  };
};

// Generate default holes structure if not provided by API
const generateDefaultHoles = (totalPar) => {
  const holes = [];
  const frontNinePar = Math.floor(totalPar / 2);
  const backNinePar = totalPar - frontNinePar;
  
  // Front 9
  for (let i = 1; i <= 9; i++) {
    holes.push({
      holeNumber: i,
      par: i <= 6 ? frontNinePar / 6 : Math.ceil(frontNinePar / 6),
      index: i
    });
  }
  
  // Back 9
  for (let i = 10; i <= 18; i++) {
    holes.push({
      holeNumber: i,
      par: i <= 15 ? backNinePar / 6 : Math.ceil(backNinePar / 6),
      index: i - 9
    });
  }
  
  return holes;
};

// Test function to verify API connection
export const testAPIConnection = async () => {
  console.log('Testing Golf Course API connection...');
  console.log('API Key:', API_KEY);
  console.log('Base URL:', GOLF_COURSE_API_BASE_URL);
  
  // Test with Moors Valley Golf Course
  const result = await testGolfCourseAPI('Moors Valley Golf Course', 'Ringwood', 'UK');
  
  if (result.success) {
    console.log('✅ API connection successful!');
    console.log('Found course data:', result.data);
    
    // Process the courses array if it exists
    if (result.data.courses && Array.isArray(result.data.courses)) {
      const convertedCourses = result.data.courses.map(convertAPICourseToInternal);
      console.log('Converted courses:', convertedCourses);
      return { ...result, convertedCourses };
    }
    
    return result;
  } else {
    console.log('❌ API connection failed:', result.error);
    return result;
  }
};
